import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  ReplayOutlined as RetryIcon,
} from '@mui/icons-material';
import { ingestionAPI } from '../../API/ingestionAPI';
import { fmtDateTime, TABLE_HEAD_ROW_SX } from '../../Utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const COL_SX = {
  fontSize: '0.725rem',
  fontWeight: 700,
  color: '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  py: 1.25,
  whiteSpace: 'nowrap',
  background: '#f9fafb',
};

const STALE_THRESHOLD_MS = 5 * 60 * 1000;

const RUN_STATUS_CONFIG = {
  SUCCESS: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', dot: '#22c55e' },
  PARTIAL: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', dot: '#f59e0b' },
  IN_PROGRESS: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6' },
};

const DEVICE_STATUS_CONFIG = {
  SUCCESS: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', dot: '#22c55e' },
  FAILED: { bg: '#fef2f2', color: '#e2453b', border: '#fadcd9', dot: '#ef4444' },
  IN_PROGRESS: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6' },
  SKIPPED_CIRCUIT: { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe', dot: '#8b5cf6' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getRunStatus = (run) => {
  if (!run.completedAt) return 'IN_PROGRESS';
  if (run.isPartial) return 'PARTIAL';
  return 'SUCCESS';
};

const formatDuration = (startedAt, completedAt) => {
  if (!completedAt) return '—';
  const ms = new Date(completedAt) - new Date(startedAt);
  if (ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
};

const formatPeriodTime = (iso, referenceDate) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  if (referenceDate) {
    const ref = new Date(referenceDate);
    if (!isNaN(ref.getTime()) && d.toDateString() !== ref.toDateString()) {
      return fmtDateTime(iso);
    }
  }
  return `${h}:${m}`;
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status, configMap, label }) => {
  const cfg = configMap[status] ?? configMap.IN_PROGRESS;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        px: '8px',
        py: '3px',
        borderRadius: '999px',
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        fontSize: 12.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
      {label}
    </Box>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string,
  configMap: PropTypes.object,
  label: PropTypes.string,
};

// ─── Device results sub-table (lazy loaded on expand) ────────────────────────

const DeviceResultsRow = ({ runId }) => {
  const { t } = useTranslation('ingestion');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const rowsPerPage = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ingestionAPI.listDeviceResults(runId, { page: page + 1, itemsPerPage: rowsPerPage });
      if (!res || res.error) {
        toast.error(t('feedback.errorLoadingDevices'));
        return;
      }
      setResults(res.value?.items ?? []);
      setTotal(res.value?.totalCount ?? 0);
    } catch {
      toast.error(t('feedback.errorLoadingDevices'));
    } finally {
      setLoading(false);
    }
  }, [runId, page, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafafa', borderTop: '1px solid', borderColor: 'divider' }}>
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: 12.5, borderColor: 'divider' } }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  ...COL_SX,
                  fontSize: '0.6rem',
                  py: 1,
                  bgcolor: '#f3f4f6',
                },
              }}
            >
              <TableCell sx={{ pl: 1.5 }}>{t('deviceTable.device')}</TableCell>
              <TableCell>{t('deviceTable.mac')}</TableCell>
              <TableCell>{t('deviceTable.status')}</TableCell>
              <TableCell>{t('deviceTable.duration')}</TableCell>
              <TableCell>{t('deviceTable.error')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 3, textAlign: 'center', border: 'none' }}>
                  <CircularProgress size={18} />
                </TableCell>
              </TableRow>
            )}
            {!loading && results.map((r) => (
              <TableRow
                key={r.id}
                sx={{ '&:last-child .MuiTableCell-root': { borderBottom: 'none' }, '&:hover': { bgcolor: '#f3f4f6' } }}
              >
                <TableCell sx={{ pl: 1.5 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.primary' }}>
                    {r.deviceName || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'text.secondary' }}>
                    {r.macAddress || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={r.status}
                    configMap={DEVICE_STATUS_CONFIG}
                    label={t(`deviceStatus.${r.status}`, r.status)}
                  />
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {formatDuration(r.startedAt, r.completedAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {r.errorMessage ? (
                    <Tooltip title={r.errorMessage} arrow>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: 'error.main',
                          maxWidth: 280,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'help',
                        }}
                      >
                        {r.errorMessage}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>—</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && results.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 3, textAlign: 'center', border: 'none' }}>
                  <Typography variant="body2" color="text.disabled">{t('deviceTable.empty')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {total > rowsPerPage && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          sx={{ borderTop: '1px solid', borderColor: 'divider', fontSize: 12 }}
        />
      )}
    </Box>
  );
};

DeviceResultsRow.propTypes = {
  runId: PropTypes.string.isRequired,
};

// ─── Expandable run row ───────────────────────────────────────────────────────

const RunRow = ({ run, onRetry }) => {
  const { t } = useTranslation('ingestion');
  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const status = getRunStatus(run);
  const isStale = status === 'IN_PROGRESS' && Date.now() - new Date(run.startedAt).getTime() > STALE_THRESHOLD_MS;
  const canRetry = (status === 'PARTIAL' || isStale) && !run.isRetried;

  const handleRetry = async (e) => {
    e.stopPropagation();
    setRetrying(true);
    try {
      await onRetry(run.id);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <>
      <TableRow
        sx={{
          '& .MuiTableCell-root': { borderColor: open ? 'transparent' : 'divider' },
          '&:hover': { bgcolor: '#f9fafb' },
          cursor: 'pointer',
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ pl: 2.5 }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {fmtDateTime(run.startedAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {formatPeriodTime(run.periodFrom, run.startedAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {formatPeriodTime(run.periodTo, run.startedAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            {formatDuration(run.startedAt, run.completedAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
            {run.totalDevices}
          </Typography>
        </TableCell>
        <TableCell>
          <StatusBadge
            status={status}
            configMap={RUN_STATUS_CONFIG}
            label={t(`status.${status}`, status)}
          />
        </TableCell>
        <TableCell sx={{ width: 52, textAlign: 'center' }}>
          {canRetry && (
            <Tooltip title={isStale ? t('actions.retryStale') : t('actions.retry')} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={retrying}
                  onClick={handleRetry}
                  sx={{ color: isStale ? 'error.main' : 'warning.main' }}
                >
                  <RetryIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ pr: 1.5, width: 52 }}>
          <Tooltip title={open ? t('table.collapse') : t('table.expand')} arrow>
            <IconButton size="small" sx={{ color: 'text.disabled' }}>
              {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, borderColor: 'divider' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <DeviceResultsRow runId={run.id} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

RunRow.propTypes = {
  run: PropTypes.object.isRequired,
  onRetry: PropTypes.func.isRequired,
};

// ─── Main component ───────────────────────────────────────────────────────────

const IngestionRunsTable = ({
  runs = [],
  totalCount = 0,
  page = 0,
  rowsPerPage = 20,
  loading = false,
  onPageChange,
  onRowsPerPageChange,
  onRetry,
}) => {
  const { t } = useTranslation(['ingestion', 'common']);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
      }}
    >
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: 13, borderColor: 'divider' } }}>
          <TableHead>
            <TableRow sx={TABLE_HEAD_ROW_SX}>
              <TableCell sx={{ pl: 2.5, width: '22%' }}>{t('table.startedAt')}</TableCell>
              <TableCell sx={{ width: '10%' }}>{t('table.periodFrom')}</TableCell>
              <TableCell sx={{ width: '10%' }}>{t('table.periodTo')}</TableCell>
              <TableCell sx={{ width: '10%' }}>{t('table.duration')}</TableCell>
              <TableCell sx={{ width: '10%' }}>{t('table.devices')}</TableCell>
              <TableCell sx={{ width: '18%' }}>{t('table.status')}</TableCell>
              <TableCell sx={{ width: 52 }} />
              <TableCell sx={{ width: 52 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 5, textAlign: 'center', border: 'none' }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!loading && runs.map((run) => (
              <RunRow key={run.id} run={run} onRetry={onRetry} />
            ))}

            {!loading && runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 5, textAlign: 'center', border: 'none' }}>
                  <Typography variant="body2" color="text.disabled">
                    {t('table.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 50]}
        showFirstButton
        showLastButton
        labelRowsPerPage={t('common:pagination.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of')} ${count}`}
        sx={{ borderTop: '1px solid', borderColor: 'divider', fontSize: 13 }}
      />
    </Box>
  );
};

IngestionRunsTable.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.object),
  totalCount: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  loading: PropTypes.bool,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  onRetry: PropTypes.func,
};

export default IngestionRunsTable;
